import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { t } from 'i18next';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';

pdfMake.addVirtualFileSystem(pdfFonts);

const PREFIX = 'domain.mitarbeiter.rotationsplan.rotationsplaene.export';

function formatStandort(standort: Standort): string {
  return standort.storenummer
    ? `${standort.storenummer} - ${standort.name}`
    : standort.name;
}

function formatZeitraum(von: Date, bis: Date): string {
  return `${dayjs(von).format('DD.MM.YYYY')} – ${dayjs(bis).format('DD.MM.YYYY')}`;
}

function buildStationContent(station: ArbeitsbereichStation): Content {
  const detailLines: string[] = [formatZeitraum(station.von, station.bis)];
  if (station.abteilungsleiter) {
    detailLines.push(station.abteilungsleiter.getName());
  }
  detailLines.push(formatStandort(station.arbeitsbereich.standort));

  const stack: Content[] = [
    {
      columns: [
        {
          // Farbmarker analog zum Punkt in der Detailansicht
          canvas: [
            {
              type: 'ellipse',
              x: 4,
              y: 6,
              r1: 4,
              r2: 4,
              color: station.arbeitsbereich.getBorderColor(),
            },
          ],
          width: 12,
        },
        {
          text: station.arbeitsbereich.name,
          style: 'stationTitle',
          width: '*',
        },
      ],
      columnGap: 0,
    },
    { text: detailLines.join('   •   '), style: 'stationMeta' },
  ];

  if (station.lernziele && station.lernziele.length > 0) {
    stack.push({ text: station.lernziele, style: 'lernziele' });
  }

  return {
    stack,
    margin: [0, 0, 0, 12],
    unbreakable: true,
  };
}

function buildHeader(
  rotationsplan: Rotationsplan,
  pageBreakBefore = false,
): Content[] {
  return [
    {
      text: `${t('domain.mitarbeiter.rotationsplan.title')} ${rotationsplan.beruf.name}`,
      style: 'title',
      ...(pageBreakBefore ? { pageBreak: 'before' as const } : {}),
    },
    { text: rotationsplan.mitarbeiter.getName(), style: 'subtitle' },
    {
      text: t(`${PREFIX}.erstelltAm`, {
        datum: dayjs().format('DD.MM.YYYY'),
      }),
      style: 'erstelltAm',
    },
  ];
}

function buildAbschnittContent(rotationsplan: Rotationsplan): Content[] {
  const abschnitte = rotationsplan.abschnitte
    .slice()
    .sort((a, b) => a.position - b.position);

  // Bei Ausbildungsberufen heißen die Abschnitte "Lehrjahr" (analog zum Löschen-Dialog).
  const naechsterHinweisKey = rotationsplan.beruf.istAusbildung
    ? `${PREFIX}.naechstesLehrjahrHinweis`
    : `${PREFIX}.naechsterAbschnittHinweis`;

  return abschnitte
    .map((abschnitt, index) => {
      const stationen = abschnitt.stationen
        .filter(
          (station): station is ArbeitsbereichStation =>
            station instanceof ArbeitsbereichStation,
        )
        .sort((a, b) => dayjs(a.von).valueOf() - dayjs(b.von).valueOf());

      // Jeder Abschnitt beginnt auf einer eigenen Seite (außer dem ersten) und
      // wiederholt den Header mit Name, Ausbildungsberuf etc.
      const kopf = buildHeader(rotationsplan, index > 0);

      const ueberschrift: Content = {
        text: `${abschnitt.position}. ${abschnitt.name}`,
        style: 'abschnitt',
      };

      const content: Content[] = [...kopf, ueberschrift];

      if (stationen.length === 0) {
        content.push({ text: t(`${PREFIX}.keineStationen`), style: 'hinweis' });
      } else {
        content.push(...stationen.map(buildStationContent));
      }

      // Hinweis auf den Seitenumbruch – nur wenn noch ein Abschnitt folgt.
      if (index < abschnitte.length - 1) {
        content.push({
          text: t(naechsterHinweisKey),
          style: 'seitenhinweis',
        });
      }

      return content;
    })
    .flat();
}

export async function generateRotationsplanPdf(
  rotationsplan: Rotationsplan,
): Promise<void> {
  const body: Content[] =
    rotationsplan.abschnitte.length === 0
      ? [
          ...buildHeader(rotationsplan),
          { text: t(`${PREFIX}.keineAbschnitte`), style: 'hinweis' },
        ]
      : buildAbschnittContent(rotationsplan);

  const docDefinition: TDocumentDefinitions = {
    info: {
      title: `${t('domain.mitarbeiter.rotationsplan.title')} ${rotationsplan.mitarbeiter.getName()}`,
    },
    pageMargins: [40, 40, 40, 40],
    content: body,
    styles: {
      title: { fontSize: 18, bold: true },
      subtitle: { fontSize: 13, margin: [0, 4, 0, 0] },
      erstelltAm: { fontSize: 9, color: '#6b7280', margin: [0, 4, 0, 16] },
      abschnitt: { fontSize: 14, bold: true, margin: [0, 0, 0, 12] },
      stationTitle: { fontSize: 11, bold: true },
      stationMeta: { fontSize: 9, color: '#374151', margin: [12, 2, 0, 0] },
      lernziele: { fontSize: 10, margin: [12, 4, 0, 0] },
      hinweis: {
        fontSize: 10,
        italics: true,
        color: '#6b7280',
        margin: [0, 0, 0, 12],
      },
      seitenhinweis: {
        fontSize: 9,
        italics: true,
        color: '#6b7280',
        margin: [0, 8, 0, 0],
      },
    },
    defaultStyle: { fontSize: 10 },
  };

  const safeName = rotationsplan.mitarbeiter
    .getName()
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_');

  const dateiname = `Rotationsplan_${safeName}.pdf`;
  pdfMake.createPdf(docDefinition).download(dateiname);
}
